import * as Yup from 'yup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, InputAdornment, Link, ListItemText } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import {
  createPayment,
  TCreateAdvancedPayment,
  updatePayment,
} from 'src/redux/slices/advanced-payments';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IEmployeeListItem } from 'src/types/employee';
import { IAdvancedPaymentListItem } from 'src/types/advanced-payment';

import { paths } from '../../routes/paths';
import { useAppDispatcher } from '../../redux/store';

export type ConditionalSchema<T> = T extends string
  ? Yup.StringSchema
  : T extends number
  ? Yup.NumberSchema
  : T extends boolean
  ? Yup.BooleanSchema
  : T extends Record<any, any>
  ? Yup.AnyObjectSchema
  : T extends Array<any>
  ? Yup.ArraySchema<any, any>
  : Yup.AnySchema;

export type Shape<Fields> = {
  [Key in keyof Fields]: ConditionalSchema<Fields[Key]>;
};

type Props = {
  employee: IEmployeeListItem;
  currentPayment?: IAdvancedPaymentListItem;
};

export default function AdvancedPaymentNewEditForm({ employee, currentPayment }: Props) {
  const dispatch = useAppDispatcher();

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const NewEmployeeSchema = Yup.object<Shape<TCreateAdvancedPayment>>().shape({
    amountInCents: Yup.number().required('Amount is required'),
    paidDate: Yup.date().required('Paid date is required'),
  });

  const defaultValues = useMemo(
    () => ({
      amountInCents: currentPayment ? Number(currentPayment.amountInCents / 100) : 0,
      paidDate: currentPayment ? new Date(currentPayment.paidDate) : new Date(),
    }),
    [currentPayment]
  );

  const methods = useForm({
    resolver: yupResolver(NewEmployeeSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    if (currentPayment) {
      try {
        await dispatch(
          updatePayment({
            paymentID: currentPayment._id,
            payment: {
              amountInCents: data.amountInCents * 100,
              date: data.paidDate,
            },
          })
        ).unwrap();

        enqueueSnackbar('Advanced payment updated successfully');

        navigate(paths.dashboard.advancedPayments.root);
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    } else {
      try {
        await dispatch(
          createPayment({
            employeeID: employee._id,
            payment: {
              amountInCents: data.amountInCents * 100,
              date: data.paidDate,
            },
          })
        ).unwrap();

        enqueueSnackbar('Advanced payment created successfully');

        navigate(paths.dashboard.advancedPayments.root);
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Employee Information</Typography>
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Employee Name"
                  secondary={`${employee?.firstName ?? ''}${
                    employee?.lastName ? ` ${employee.lastName}` : ''
                  }`}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={4}>
                <ListItemText
                  primary="Employee Number"
                  secondary={employee?.employeeNumber}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Payment Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="amountInCents"
                  label="Advanced Amount"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                  }}
                  type="number"
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="paidDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      disableFuture
                      label="Paid Date"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <Stack direction="row" gap={2}>
                <Link component={RouterLink} href={paths.dashboard.employees.view(employee?._id)}>
                  <Button variant="outlined" color="inherit">
                    Cancel
                  </Button>
                </Link>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  onClick={() => trigger()}
                >
                  {!currentPayment ? 'Create Payment' : 'Save Changes'}
                </LoadingButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
